import { useEffect, useState } from "react";

const REGIONS = {
  ZA: { code: "ZA", flag: "/flags/za.svg", label: "South Africa" },
  IN: { code: "IN", flag: "/flags/in.svg", label: "India" },
};

const DEFAULT_REGION_CODE = "ZA";

const BOUNDS = {
  IN: { lat: [6.5, 35.7], lng: [68.1, 97.4] },
  ZA: { lat: [-34.9, -22.1], lng: [16.4, 32.9] },
};

const TIME_ZONES = {
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Africa/Johannesburg": "ZA",
};

const CACHE_KEY = "deliva:site-region";

function regionCodeFromCoords(lat, lng) {
  const match = Object.keys(BOUNDS).find((code) => {
    const { lat: [south, north], lng: [west, east] } = BOUNDS[code];
    return lat >= south && lat <= north && lng >= west && lng <= east;
  });
  return match || DEFAULT_REGION_CODE;
}

function regionCodeFromTimeZone() {
  try {
    return TIME_ZONES[Intl.DateTimeFormat().resolvedOptions().timeZone] || null;
  } catch {
    return null;
  }
}

function cachedRegionCode() {
  try {
    const code = localStorage.getItem(CACHE_KEY);
    return REGIONS[code] ? code : null;
  } catch {
    return null;
  }
}

function cacheRegionCode(code) {
  try {
    localStorage.setItem(CACHE_KEY, code);
  } catch {
    return;
  }
}

async function geolocationGranted() {
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state === "granted";
  } catch {
    return false;
  }
}

function browserCoords() {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      () => resolve(null),
      { timeout: 10000, maximumAge: 600000 }
    );
  });
}

async function detectRegionCode() {
  if ("geolocation" in navigator && (await geolocationGranted())) {
    const coords = await browserCoords();
    if (coords) return regionCodeFromCoords(coords.latitude, coords.longitude);
  }
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/misc/fetchCountryName`);
    const ip = await res.json();
    return Number.isFinite(ip.latitude) && Number.isFinite(ip.longitude)
      ? regionCodeFromCoords(ip.latitude, ip.longitude)
      : null;
  } catch {
    return null;
  }
}

export default function useSiteRegion() {
  const [code, setCode] = useState(
    () => cachedRegionCode() || regionCodeFromTimeZone() || DEFAULT_REGION_CODE
  );

  useEffect(() => {
    let active = true;
    let permission = null;
    const refresh = () => {
      detectRegionCode().then((next) => {
        if (!active || !next) return;
        setCode(next);
        cacheRegionCode(next);
      });
    };

    refresh();
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then(
        (status) => {
          if (!active) return;
          permission = status;
          status.addEventListener("change", refresh);
        },
        () => undefined
      );
    }

    return () => {
      active = false;
      if (permission) permission.removeEventListener("change", refresh);
    };
  }, []);

  return REGIONS[code];
}
